import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Checkbox from 'components/checkbox';
import Select from 'react-select';
import { allChannels, frenchAreas } from 'variables/industries';
import { useSavedRegions } from 'services/savedRegionsService';
import { useIndustries } from 'services/industryService';
import { ChannelIcon } from 'components/icons';
import { MdCheck, MdOutlineStorefront } from 'react-icons/md';
import { TbTargetArrow } from 'react-icons/tb';

const regionsSelectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 42,
        borderRadius: 8,
        borderColor: state.isFocused ? '#7464FC' : '#F3EDE4',
        boxShadow: 'none',
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        '&:hover': { borderColor: '#7464FC' },
    }),
    placeholder: (base: any) => ({ ...base, color: '#676767' }),
    multiValue: (base: any) => ({ ...base, backgroundColor: '#EAE6FF', borderRadius: 9999, paddingLeft: 4 }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: '#1C0F53',
        fontFamily: 'Geist, sans-serif',
        fontSize: 12,
        fontWeight: 600,
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: '#1C0F53',
        borderRadius: 9999,
        '&:hover': { backgroundColor: '#1C0F53', color: '#ffffff' },
    }),
    menu: (base: any) => ({
        ...base,
        borderRadius: 12,
        border: '1px solid #F3EDE4',
        boxShadow: '0 2px 8px rgba(20,20,18,0.08)',
        overflow: 'hidden',
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        backgroundColor: state.isSelected ? '#7464FC' : state.isFocused ? '#EAE6FF' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#141412',
        cursor: 'pointer',
    }),
    dropdownIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#7464FC' } }),
    clearIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#D63C3C' } }),
    indicatorSeparator: (base: any) => ({ ...base, backgroundColor: '#F3EDE4' }),
};

const SellerOffer = () => {
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [error, setError] = useState('');
    const [offerCreated, setOfferCreated] = useState(false);

    const [channels, setChannels] = useState([]);
    const [industry, setIndustry] = useState('');
    const [subIndustry, setSubIndustry] = useState('');
    const [fields, setFields] = useState(['Prénom', 'Nom', 'Téléphone', 'Email']);

    const { savedRegions, saveRegions } = useSavedRegions();
    const [offer, setOffer] = useState<any>({
        type: 'leads',
        channels: [],
        industry: '',
        subIndustry: '',
        regions: [] as string[],
        fields: ['Prénom', 'Nom', 'Téléphone', 'Email'],
        websiteInfo: '',
        weeklyQuantity: '',
        sellerPrice: '',
        franceOnly: true,
    });

    const handlechange = (e: any) => {
        const { id, value } = e.target;
        setError('');
        if (id === 'weeklyQuantity' && Number(value) < 40) {
            setOffer({ ...offer, [id]: 40 });
            setError('La quantite minimale est de 40 leads');
        } else if (id === 'weeklyQuantity' && Number(value) > 500) {
            setOffer({ ...offer, [id]: 500 });
            setError('La quantite maximale est de 500 leads');
        } else {
            setOffer({ ...offer, [id]: value });
        }
    };

    const handleFieldsChange = (e: any) => {
        const { id, checked } = e.target;
        const priority = ['Prénom', 'Nom', 'Téléphone', 'Email'];
        const newFields = checked
            ? [...new Set([...priority, ...fields, id])]
            : fields.filter((f) => f !== id);
        setFields(newFields);
        handlechange({ target: { id: 'fields', value: newFields } });
    };

    const handleChannelChange = (e: any) => {
        const { id, checked } = e.target;
        const newChannels = checked ? [...channels, id] : channels.filter((c: string) => c !== id);
        setChannels(newChannels);
        handlechange({ target: { id: 'channels', value: newChannels } });
    };

    const handleIndustryChange = (e: any) => {
        const { value } = e.target;
        window.localStorage.setItem('industry', value);
        const selectedIndustry = industriesData.find((i) => i.name === value);
        setIndustry(value);
        if (selectedIndustry && selectedIndustry.subIndustries.length > 0) {
            setSubIndustry(selectedIndustry.subIndustries[0].name);
            setOffer({ ...offer, industry: value, subIndustry: selectedIndustry.subIndustries[0].name });
        } else {
            setSubIndustry('');
            setOffer({ ...offer, industry: value, subIndustry: '' });
        }
    };

    const handleSubIndustryChange = (e: any) => {
        const { value } = e.target;
        setSubIndustry(value);
        handlechange(e);
    };

    useEffect(() => {
        if (industriesData.length > 0 && !industry) {
            const first = industriesData[0];
            const firstSub = first.subIndustries?.[0]?.name ?? '';
            setIndustry(first.name);
            setSubIndustry(firstSub);
            setOffer((prev: any) => ({ ...prev, industry: first.name, subIndustry: firstSub }));
            window.localStorage.setItem('industry', first.name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData]);

    useEffect(() => {
        if (savedRegions.length > 0) {
            const isDefault =
                offer.regions.length === 0 ||
                (offer.regions.length === 1 && offer.regions[0] === 'Toute la France');
            if (isDefault) {
                handlechange({ target: { id: 'regions', value: savedRegions } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedRegions]);

    const createOffer = () => {
        if (offerCreated) return;
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        setOfferCreated(true);
        axios
            .post(`${CONFIG.apiUrl}/seller-offer`, offer, config)
            .then(() => {
                window.location.href = '/my-offer?created=true';
            })
            .catch((err: any) => {
                setError(err.response.data.error);
                setOfferCreated(false);
            });
    };

    const handleSubmit = () => {
        if (offer.regions.length === 0) {
            setError('Veuillez selectionner au moins un departement');
            return;
        }
        const hasCountryWideSelection = offer.regions.some((region: string) => region.includes('Toute'));
        const hasClimateZone = offer.regions.some((region: string) => region.includes('Zone Climatique'));
        if (offer.regions.length < 7 && !hasCountryWideSelection && !hasClimateZone) {
            setError("Si vous operez sur moins de 7 departements ou si vous souhaitez passer une commande inferieure a 40 leads, nous vous recommandons de proceder via un ordre d'achat.");
            return;
        }
        if (offer.channels.length === 0) {
            setError('Veuillez remplir tous les champs obligatoires');
            return;
        }
        createOffer();
    };

    const inputCls = 'w-full rounded-lg border border-leadrs-stroke bg-white px-3 py-2.5 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none transition-colors';
    const labelCls = 'font-geist text-sm font-medium text-leadrs-text';
    const hintCls = 'mt-1 font-geist text-xs text-leadrs-text-soft';

    if (industriesLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-leadrs-brand" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Page header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                <div className="flex-1 rounded-xl border border-leadrs-stroke bg-white p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brown-soft">
                            <MdOutlineStorefront className="h-5 w-5 text-leadrs-brown-focus" />
                        </div>
                        <div>
                            <h1 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                                Créer une offre
                            </h1>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Publiez votre offre sur la marketplace et recevez des acheteurs qualifiés.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-3 lg:w-80 lg:self-start">
                    <div className="rounded-xl bg-leadrs-blue-soft p-4">
                        <div className="mb-3 flex items-center gap-2">
                            <TbTargetArrow size={16} className="text-leadrs-blue-focus" />
                            <p className="font-geist text-sm font-semibold text-leadrs-blue-focus">Comment ça marche ?</p>
                        </div>
                        <ul className="flex flex-col gap-1.5">
                            {[
                                'Définissez votre industrie, canaux et zones géographiques',
                                'Fixez votre prix par lead et la quantité souhaitée',
                                'Votre offre est publiée sur la marketplace',
                                'Les acheteurs souscrivent et les leads commencent à arriver',
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2">
                                    <MdCheck size={14} className="mt-0.5 flex-shrink-0 text-leadrs-blue-focus" />
                                    <span className="font-geist text-xs text-leadrs-blue-focus opacity-80">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Form card */}
            <div className="rounded-xl border border-leadrs-stroke bg-white">
                {/* Error */}
                {error && (
                    <div className="mx-6 mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3">
                        <p className="font-geist text-sm text-leadrs-red-focus">{error}</p>
                    </div>
                )}

                {/* Section Offre */}
                <div className="border-b border-leadrs-stroke px-6 pt-5 pb-3">
                    <h2 className="font-crimson text-lg font-semibold text-leadrs-text">Offre</h2>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 p-6 md:grid-cols-2">
                    <div>
                        <label className={labelCls}>Departements *</label>
                        <div className="mt-2">
                            <Select
                                value={offer.regions.map((region: string) => ({
                                    value: region === 'Toute la France' ? 'france' : region,
                                    label: region,
                                }))}
                                isMulti
                                onChange={(items) => {
                                    const regions: string[] = items.map((item: any) =>
                                        item.value === 'france' ? 'Toute la France' : item.value
                                    );
                                    handlechange({ target: { id: 'regions', value: regions } });
                                    saveRegions(regions);
                                }}
                                options={frenchAreas.map((item) => ({
                                    value: item === 'Toute la France' ? 'france' : item,
                                    label: item,
                                }))}
                                className="w-full"
                                classNamePrefix="select"
                                styles={regionsSelectStyles}
                            />
                        </div>
                        <p className={hintCls}>Minimum 7 departements</p>
                    </div>

                    <div>
                        <label className={labelCls}>Industrie *</label>
                        <select id="industry" value={industry} className={`mt-2 ${inputCls}`} onChange={handleIndustryChange}>
                            {industriesData.map((item) => (
                                <option key={item._id} value={item.name}>{item.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={labelCls}>Canaux d&apos;acquisition *</label>
                        <div className="mt-3 grid grid-cols-2 gap-2 xl:grid-cols-3">
                            {allChannels.map((channel) => (
                                <div key={channel} className="flex items-center gap-1.5">
                                    <Checkbox
                                        defaultChecked={false}
                                        id={channel}
                                        onChange={handleChannelChange}
                                    />
                                    <ChannelIcon channel={channel} />
                                    <p className="font-geist text-xs text-leadrs-text">{channel}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-5">
                        <div>
                            <label className={labelCls}>Produit *</label>
                            <select id="subIndustry" value={subIndustry} className={`mt-2 ${inputCls}`} onChange={handleSubIndustryChange}>
                                {industriesData
                                    .find((i) => i.name === industry)
                                    ?.subIndustries.map((item: any) => (
                                        <option key={item.name} value={item.name}>{item.name}</option>
                                    ))}
                            </select>
                        </div>

                        {(industry === 'Autre' || subIndustry === 'Autre') && (
                            <div className="rounded-xl bg-leadrs-yellow-soft p-4">
                                <p className="font-geist text-sm text-leadrs-yellow-focus">
                                    Contactez charlotte@leadrs.co pour ajouter votre industrie sur Leadrs.
                                </p>
                            </div>
                        )}

                        {industriesData
                            .find((i) => i.name === industry)
                            ?.subIndustries.find((item: any) => item.name === subIndustry)
                            ?.sub?.length > 0 && (
                            <div>
                                <label className={labelCls}>Details sous-industrie *</label>
                                <select id="subIndustry" className={`mt-2 ${inputCls}`} onChange={handlechange}>
                                    {industriesData
                                        .find((i) => i.name === industry)
                                        ?.subIndustries.find((item: any) => item.name === subIndustry)
                                        ?.sub?.map((subItem: string) => (
                                            <option key={subItem} value={subItem}>{subItem}</option>
                                        ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section Pricing */}
                <div className="border-t border-leadrs-stroke px-6 pt-5 pb-3">
                    <h2 className="font-crimson text-lg font-semibold text-leadrs-text">Pricing</h2>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <div>
                            <label className={labelCls}>Prix par lead *</label>
                            <input
                                id="sellerPrice"
                                type="number"
                                placeholder="260"
                                onChange={handlechange}
                                className={`mt-2 ${inputCls}`}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Quantite de leads (min 40, max 500)</label>
                            <input
                                id="weeklyQuantity"
                                type="number"
                                placeholder="250"
                                onChange={handlechange}
                                className={`mt-2 ${inputCls}`}
                            />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className={labelCls}>Criteres de votre lead</label>
                        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                            {(() => {
                                const priority = ['Prénom', 'Nom', 'Téléphone', 'Email'];
                                const extra = (industriesData.find((i: any) => i.name === industry)?.fields || [])
                                    .filter((f: string) => !priority.includes(f));
                                return [...priority, ...extra].map((field: string) => (
                                    <div key={field} className="flex items-center gap-2">
                                        <Checkbox
                                            checked={fields.includes(field)}
                                            defaultChecked={priority.includes(field)}
                                            id={field}
                                            extra="mt-1"
                                            onChange={handleFieldsChange}
                                        />
                                        <p className="font-geist text-sm text-leadrs-text">{field}</p>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end border-t border-leadrs-stroke px-6 py-4">
                    <button
                        onClick={handleSubmit}
                        disabled={offerCreated}
                        className="rounded-lg bg-leadrs-brand px-5 py-2.5 font-geist text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                        {offerCreated ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SellerOffer;
